import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Banknote, CheckCircle2, Loader2, Zap } from "lucide-react";
import { toast, Toaster } from "sonner";

const STATUS_BADGE = {
  flash_approved: "badge-flash",
  approved: "badge-success",
};

export default function Payouts() {
  const [data, setData] = useState({ pending: [], pending_count: 0, pending_amount_kes: 0,
                                     paid_total_count: 0, paid_total_amount_kes: 0, recently_paid: [] });
  const [busyId, setBusyId] = useState(null);
  const [autoBusy, setAutoBusy] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [threshold, setThreshold] = useState(5000);
  const [loading, setLoading] = useState(true);
  const [checked, setChecked] = useState(new Set());

  function load() {
    setLoading(true);
    api.get("/admin/payouts")
      .then((r) => { setData(r.data); setChecked(new Set()); })
      .finally(() => setLoading(false));
  }
  useEffect(load, []);

  function toggle(id, on) {
    setChecked((p) => { const n = new Set(p); on ? n.add(id) : n.delete(id); return n; });
  }
  function toggleAll() {
    const ids = data.pending.map((c) => c.id);
    setChecked((p) => p.size === ids.length && ids.length > 0 ? new Set() : new Set(ids));
  }

  async function release(claim) {
    setBusyId(claim.id);
    try {
      await api.post(`/admin/payouts/${claim.id}/release`);
      toast.success(`Payout released for ${claim.claim_number}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusyId(null);
    }
  }

  async function bulkRelease() {
    setBulkBusy(true);
    try {
      const { data: r } = await api.post("/admin/payouts/bulk-release", {
        claim_ids: Array.from(checked),
      });
      toast.success(`Released ${r.released_count}/${r.attempted} payout${r.attempted === 1 ? "" : "s"}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBulkBusy(false);
    }
  }

  async function autoPayout() {
    setAutoBusy(true);
    try {
      const { data: r } = await api.post("/admin/auto-payout", null, { params: { threshold } });
      if (r.released_count === 0) {
        toast.message("Nothing to auto-release", { description: `No flash-approved claims under KES ${threshold.toLocaleString()}.` });
      } else {
        toast.success(`Auto-released ${r.released_count} claim${r.released_count === 1 ? "" : "s"} · KES ${r.released_amount_kes.toLocaleString()}`);
      }
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setAutoBusy(false);
    }
  }

  return (
    <div className="p-8 space-y-6" data-testid="payouts-page">
      <Toaster position="top-right" />
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Banknote className="w-6 h-6 text-[#114232]" />
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#c15337]">Finance</div>
            <h1 className="font-heading text-3xl font-bold mt-1 text-[#114232]">Claim Payouts Queue</h1>
            <p className="text-sm text-[#5c6360] mt-1">Approved claims awaiting M-Pesa B2C disbursement.</p>
          </div>
        </div>
        <div className="flex items-end gap-2" data-testid="auto-payout-controls">
          <div className="flex flex-col gap-1">
            <label htmlFor="auto-payout-threshold" className="text-[10px] uppercase tracking-[0.18em] text-[#5c6360] font-semibold">
              Auto-release threshold (KES)
            </label>
            <input
              id="auto-payout-threshold"
              type="number"
              min={100}
              max={100000}
              step={500}
              value={threshold}
              onChange={(e) => setThreshold(Math.max(100, Math.min(100000, Number(e.target.value) || 0)))}
              data-testid="auto-payout-threshold-input"
              className="w-36 h-9 px-3 text-sm rounded-md border border-[#114232]/15 bg-white font-mono"
            />
          </div>
          <Button
            onClick={autoPayout}
            disabled={autoBusy}
            data-testid="auto-payout-btn"
            className="btn-terracotta rounded-full px-5 h-9 text-xs hover:bg-[var(--terracotta-hover)]"
          >
            {autoBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Zap className="w-3.5 h-3.5 mr-1.5" />}
            Auto-release ≤ KES {threshold.toLocaleString()}
          </Button>
        </div>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="metric-card" data-testid="payouts-pending-count">
          <div className="text-xs uppercase tracking-[0.18em] text-stone-500 font-semibold">Pending</div>
          <div className="mt-3 text-3xl font-bold font-mono">{data.pending_count}</div>
          <div className="text-xs text-muted-foreground mt-1">Awaiting release</div>
        </div>
        <div className="metric-card" data-testid="payouts-pending-amount">
          <div className="text-xs uppercase tracking-[0.18em] text-stone-500 font-semibold">Pending value</div>
          <div className="mt-3 text-2xl font-bold font-mono">KES {data.pending_amount_kes.toLocaleString()}</div>
          <div className="text-xs text-muted-foreground mt-1">In the queue</div>
        </div>
        <div className="metric-card" data-testid="payouts-paid-count">
          <div className="text-xs uppercase tracking-[0.18em] text-stone-500 font-semibold">Paid all-time</div>
          <div className="mt-3 text-3xl font-bold font-mono">{data.paid_total_count}</div>
          <div className="text-xs text-muted-foreground mt-1">Claims settled</div>
        </div>
        <div className="metric-card" data-testid="payouts-paid-amount">
          <div className="text-xs uppercase tracking-[0.18em] text-stone-500 font-semibold">Paid amount</div>
          <div className="mt-3 text-2xl font-bold font-mono">KES {data.paid_total_amount_kes.toLocaleString()}</div>
          <div className="text-xs text-muted-foreground mt-1">Cumulative disbursed</div>
        </div>
      </section>

      <div className="flex items-center gap-2 flex-wrap" data-testid="payouts-bulk-controls">
        <button
          onClick={toggleAll}
          data-testid="payouts-select-all"
          disabled={data.pending.length === 0}
          className="px-3 py-1.5 rounded-md border border-[#114232]/15 bg-white hover:bg-[#114232]/5 text-[#114232] uppercase tracking-wider text-[11px] font-semibold disabled:opacity-40"
        >
          {checked.size === data.pending.length && data.pending.length > 0 ? "Clear" : "Select all"}
        </button>
        <span className="text-xs text-[#5c6360] font-mono" data-testid="payouts-bulk-count">
          {checked.size} selected
        </span>
        <Button
          onClick={bulkRelease}
          disabled={bulkBusy || checked.size === 0}
          data-testid="payouts-bulk-release-btn"
          className="btn-baobab rounded-full px-4 h-9 text-xs hover:bg-[var(--baobab-hover)]"
        >
          {bulkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
          Bulk release ({checked.size})
        </Button>
      </div>

      <div className="bg-white border border-border rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid="payouts-table">
          <thead className="text-xs uppercase tracking-wider text-stone-500">
            <tr>
              <th className="text-left font-medium py-3 px-3 w-10"></th>
              <th className="text-left font-medium py-3 px-5">Claim #</th>
              <th className="text-left font-medium py-3 px-5">Merchant</th>
              <th className="text-left font-medium py-3 px-5">Type</th>
              <th className="text-left font-medium py-3 px-5">Payout</th>
              <th className="text-left font-medium py-3 px-5">Status</th>
              <th className="text-right font-medium py-3 px-5">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="py-10 text-center text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
              </td></tr>
            )}
            {!loading && data.pending.map((c) => (
              <tr key={c.id} className="border-t border-border" data-testid={`payout-row-${c.id}`}>
                <td className="py-3 px-3">
                  <input
                    type="checkbox"
                    checked={checked.has(c.id)}
                    onChange={(e) => toggle(c.id, e.target.checked)}
                    data-testid={`payout-check-${c.id}`}
                    className="h-4 w-4 accent-[#c15337] cursor-pointer"
                  />
                </td>
                <td className="py-3 px-5 font-mono text-xs">{c.claim_number}</td>
                <td className="py-3 px-5">
                  <div className="font-medium">{c.merchant?.business_name || "—"}</div>
                  <div className="text-xs text-muted-foreground font-mono">{c.merchant?.phone || c.merchant_id?.slice(0, 8)}</div>
                </td>
                <td className="py-3 px-5 capitalize">{c.claim_type?.replace(/_/g, " ")}</td>
                <td className="py-3 px-5 font-mono font-semibold">KES {(c.payout_amount || c.amount_claimed).toLocaleString()}</td>
                <td className="py-3 px-5">
                  <span className={`badge-status ${STATUS_BADGE[c.status] || "badge-pending"}`}>
                    {c.status?.replace(/_/g, " ")}
                  </span>
                </td>
                <td className="py-3 px-5 text-right">
                  <Button
                    onClick={() => release(c)}
                    disabled={busyId === c.id}
                    data-testid={`release-payout-${c.id}`}
                    className="btn-baobab rounded-full px-4 h-8 text-xs hover:bg-[var(--baobab-hover)]"
                  >
                    {busyId === c.id
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                      : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
                    Release payout
                  </Button>
                </td>
              </tr>
            ))}
            {!loading && data.pending.length === 0 && (
              <tr><td colSpan={7} className="py-10 text-center text-muted-foreground">
                All claims settled — payouts queue is empty.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {data.recently_paid?.length > 0 && (
        <div className="bg-white border border-border rounded-md" data-testid="recently-paid-card">
          <div className="px-5 py-4 border-b border-border font-heading text-base font-semibold">Recently paid</div>
          <table className="w-full text-sm">
            <tbody>
              {data.recently_paid.map((c) => (
                <tr key={c.id} className="border-t border-border" data-testid={`recently-paid-${c.id}`}>
                  <td className="py-3 px-5 font-mono text-xs">{c.claim_number}</td>
                  <td className="py-3 px-5 font-mono">KES {(c.payout_amount || 0).toLocaleString()}</td>
                  <td className="py-3 px-5 font-mono text-xs text-muted-foreground">{c.payout_reference}</td>
                  <td className="py-3 px-5 text-xs text-muted-foreground">
                    {c.decided_at ? new Date(c.decided_at).toLocaleString() : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
